import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export type EntityTypeSlug =
  | 'character'
  | 'location'
  | 'item'
  | 'creature'
  | 'artifact';

export type LibraryScopeSlug = 'private' | 'shared' | 'book';

export interface EntitySummaryDto {
  id: string;
  projectId?: string | null;
  name: string;
  entityType: EntityTypeSlug;
  description?: string | null;
  libraryScope: LibraryScopeSlug;
  imageCount: number;
  descriptorCount: number;
  hasApprovedLora: boolean;
  referenceImageId?: string | null;
  referenceImageUrl?: string | null;
  referenceImageThumbnailUrl?: string | null;
  updatedAt: string;
}

export interface EntityDescriptorDto {
  id: string;
  entityId: string;
  category: string;
  content: string;
  sequenceOrder: number;
}

export interface EntityImageDto {
  id: string;
  entityId: string;
  imageId: string;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  isReference: boolean;
  sequenceOrder: number;
  generatedCaption?: string | null;
}

export interface EntityDto {
  id: string;
  projectId?: string | null;
  name: string;
  entityType: EntityTypeSlug;
  description?: string | null;
  libraryScope: LibraryScopeSlug;
  sourceEntityId?: string | null;
  descriptors: EntityDescriptorDto[];
  images: EntityImageDto[];
  hasApprovedLora: boolean;
  referenceImageId?: string | null;
  voiceProfile?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface EntitySuggestionDto {
  id: string;
  name: string;
  entityType: EntityTypeSlug;
  description?: string | null;
  score: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface EntityListFilters {
  scope?: LibraryScopeSlug;
  projectId?: string;
  type?: EntityTypeSlug;
  search?: string;
  page?: number;
  pageSize?: number;
}

export function useEntities(filters: EntityListFilters) {
  const { getToken } = useAuth();
  return useQuery<{ items: EntitySummaryDto[]; total: number }>({
    queryKey: ['entities', filters],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const search = new URLSearchParams();
      if (filters.scope) search.set('scope', filters.scope);
      if (filters.projectId) search.set('project_id', filters.projectId);
      if (filters.type) search.set('type', filters.type);
      if (filters.search) search.set('search', filters.search);
      if (filters.page) search.set('page', String(filters.page));
      if (filters.pageSize) search.set('page_size', String(filters.pageSize));
      const response = await client
        .get(`api/v2/entities?${search.toString()}`)
        .json<ApiEnvelope<EntitySummaryDto[]>>();
      return {
        items: response.data ?? [],
        total: response.pagination?.total ?? response.data.length,
      };
    },
  });
}

export function useEntity(id: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<EntityDto>({
    enabled: Boolean(id),
    queryKey: ['entity', id],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/entities/${id}`)
        .json<ApiEnvelope<EntityDto>>();
      return response.data;
    },
  });
}

export interface CreateEntityInput {
  name: string;
  entityType: EntityTypeSlug;
  description?: string;
  projectId?: string | null;
  libraryScope?: LibraryScopeSlug;
  skipDuplicateCheck?: boolean;
}

export interface DuplicateResult {
  kind: 'duplicates';
  suggestions: EntitySuggestionDto[];
}

export interface CreatedResult {
  kind: 'created';
  entity: EntityDto;
}

export function useCreateEntity() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreateEntityInput,
    ): Promise<CreatedResult | DuplicateResult> => {
      const client = createApiClient(getToken);
      const response = await client.post('api/v2/entities', {
        json: input,
        throwHttpErrors: false,
      });
      if (response.status === 409) {
        const body = (await response.json()) as ApiEnvelope<{
          created: null;
          suggestions: EntitySuggestionDto[];
        }>;
        return { kind: 'duplicates', suggestions: body.data.suggestions };
      }
      if (!response.ok) {
        throw new Error(`Create failed: ${response.status}`);
      }
      const body = (await response.json()) as ApiEnvelope<EntityDto>;
      return { kind: 'created', entity: body.data };
    },
    onSuccess: (result) => {
      if (result.kind === 'created') {
        void qc.invalidateQueries({ queryKey: ['entities'] });
      }
    },
  });
}

export interface UpdateEntityInput {
  id: string;
  name: string;
  description?: string;
  entityType?: EntityTypeSlug;
  libraryScope?: LibraryScopeSlug;
}

export function useUpdateEntity() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateEntityInput) => {
      const client = createApiClient(getToken);
      const response = await client
        .put(`api/v2/entities/${input.id}`, {
          json: input,
        })
        .json<ApiEnvelope<EntityDto>>();
      return response.data;
    },
    onSuccess: (dto) => {
      qc.setQueryData(['entity', dto.id], dto);
      void qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

export function useDeleteEntity() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/entities/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

export function useAddDescriptor(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      category: string;
      content: string;
      sequenceOrder?: number;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/entities/${entityId}/descriptors`, { json: input })
        .json<ApiEnvelope<EntityDescriptorDto>>();
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useUpdateDescriptor(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      category: string;
      content: string;
      sequenceOrder: number;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .put(`api/v2/entity-descriptors/${input.id}`, { json: input })
        .json<ApiEnvelope<EntityDescriptorDto>>();
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useDeleteDescriptor(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/entity-descriptors/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useAttachEntityImage(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      imageId: string;
      isReference?: boolean;
      sequenceOrder?: number;
    }) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/entities/${entityId}/images`, { json: input })
        .json<ApiEnvelope<EntityImageDto>>();
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useSetReferenceImage(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entityImageId: string) => {
      const client = createApiClient(getToken);
      await client.post(
        `api/v2/entities/${entityId}/images/${entityImageId}/reference`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useRemoveEntityImage(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entityImageId: string) => {
      const client = createApiClient(getToken);
      await client.delete(
        `api/v2/entities/${entityId}/images/${entityImageId}`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useBatchRemoveEntityImages(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entityImageIds: string[]) => {
      const client = createApiClient(getToken);
      for (const id of entityImageIds) {
        await client.delete(`api/v2/entities/${entityId}/images/${id}`);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useUpdateImageCaption(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entityImageId,
      caption,
    }: {
      entityImageId: string;
      caption: string | null;
    }) => {
      const client = createApiClient(getToken);
      await client.put(
        `api/v2/entities/${entityId}/images/${entityImageId}/caption`,
        { json: { caption } },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

// --- Sprint 3 hooks ---

export function useBatchAddDescriptors(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      descriptors: { category: string; content: string }[],
    ) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/entities/${entityId}/descriptors/batch`, {
        json: { descriptors },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useReorderDescriptors(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/entities/${entityId}/descriptors/reorder`, {
        json: { orderedIds },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
}

export function useBatchDeleteEntities() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const client = createApiClient(getToken);
      await client.post('api/v2/entities/batch-delete', { json: { ids } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

export interface ArchetypeDto {
  id: string;
  name: string;
  entityType: string;
  descriptors: unknown;
  source: string;
}

export function useEntityArchetypes() {
  const { getToken } = useAuth();
  return useQuery<ArchetypeDto[]>({
    queryKey: ['entity-archetypes'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/entities/archetypes')
        .json<ApiEnvelope<ArchetypeDto[]>>();
      return resp.data ?? [];
    },
  });
}

export const ENTITY_TYPES: { value: EntityTypeSlug; label: string }[] = [
  { value: 'character', label: 'Character' },
  { value: 'location', label: 'Location' },
  { value: 'item', label: 'Item' },
  { value: 'creature', label: 'Creature' },
  { value: 'artifact', label: 'Artifact' },
];

export const DESCRIPTOR_CATEGORIES_BY_TYPE: Record<EntityTypeSlug, string[]> = {
  character: [
    // structured fields
    'gender',
    'species',
    'sexual_orientation',
    'character_archetype',
    'height',
    'weight',
    'body_type',
    'skin_color',
    'eye_color',
    'hair_color',
    'hair_length',
    'hair_style',
    'virtues',
    'flaws',
    'magic_powers',
    'physical_abilities',
    'languages',
    // free-form
    'appearance',
    'voice',
    'attire',
    'personality',
    'background',
    'motivations',
  ],
  location: [
    'climate',
    'architecture',
    'atmosphere',
    'landmarks',
    'inhabitants',
  ],
  item: ['material', 'color', 'size', 'purpose', 'enchantments'],
  creature: ['appearance', 'abilities', 'habitat', 'behavior', 'size'],
  artifact: ['material', 'origin', 'purpose', 'enchantments', 'size'],
};
